import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  input,
  output,
  signal,
  viewChild
} from "@angular/core";
import { Subject, debounceTime, takeUntil } from "rxjs";
import { GoogleMap } from "@angular/google-maps";
import { ILocation } from "src/app/interfaces/interfaces";
import { ImageResolverPipe } from "src/app/pipes/sharedpipes/image-resolver.pipe";
import { IconService } from "src/app/services/icon.service";
import { UrlService } from "src/app/services/url.service";
import { IconComponent } from "../icon/icon.component";

@Component({
  selector: "app-map",
  templateUrl: "./map.component.html",
  styleUrl: "./map.component.scss",
  imports: [IconComponent, GoogleMap],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  mapLocation = signal<ILocation>(null);
  public zoom = 10;
  mapEnabled = signal(true);

  public mapOptions = {
    clickableIcons: false,
    fullscreenControl: false,
    streetViewControl: false,
    zoomControl: false,
    mapTypeControl: false,
    scrollwheel: true
  };

  public noSelectionIconUrl = "";
  public selectedIconUrl = "";
  public clientIconUrl = "";

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private clusterer: any = null;
  private legacyMarkers: Map<ILocation, google.maps.Marker> = new Map();
  private clientLegacyMarker: google.maps.Marker | null = null;
  private googleMap: google.maps.Map | null = null;

  public readonly googleMapRef = viewChild<GoogleMap>("googleMap");

  allLocations = input<ILocation[] | undefined>();
  clientLocation = input<ILocation>();
  selectedLocation = input<ILocation | undefined>();

  locationSelected = output<ILocation>();
  locationSearched = output<ILocation>();
  locationIdled = output<ILocation>();

  public readonly searchElementRef = viewChild<ElementRef>("search");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  suggestions = signal<any[]>([]);

  private readonly searchSubject = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private ngZone: NgZone,
    public iconService: IconService,
    private urlService: UrlService
  ) {
    this.searchSubject
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((value) => this.executeSearch(value));
  }

  async ngOnInit(): Promise<void> {
    this.init();

    const imageResolver = new ImageResolverPipe();
    this.noSelectionIconUrl = imageResolver.transform(
      "/assets/icons/map/no-place-24px.svg"
    );
    this.selectedIconUrl = imageResolver.transform(
      "/assets/icons/map/" + this.urlService.brand + "/place-24px.svg"
    );
    this.clientIconUrl = imageResolver.transform(
      "/assets/icons/map/" + this.urlService.brand + "/client_location.svg"
    );

    // Load core so google.maps.Size is available as a constructor
    await google.maps.importLibrary("core");

    this.googleMap = await this.waitForMap();
    this.renderMarkers();
  }

  private async waitForMap() {
    return new Promise<google.maps.Map>((resolve) => {
      const check = () => {
        const map = this.googleMapRef()?.googleMap;
        if (map) return resolve(map);
        setTimeout(check, 50);
      };
      check();
    });
  }

  private renderMarkers(): void {
    // Clear existing markers and clusterer
    this.clusterer?.clearMarkers();
    this.clientLegacyMarker?.setMap(null);
    this.legacyMarkers.forEach((m) => m.setMap(null));
    this.legacyMarkers.clear();

    const markers: google.maps.Marker[] = [];

    for (const location of this.allLocations() ?? []) {
      const selected = this.locationMatches(location, this.selectedLocation());
      const marker = new google.maps.Marker({
        position: { lat: location.latitude, lng: location.longitude },
        icon: {
          url: selected ? this.selectedIconUrl : this.noSelectionIconUrl,
          scaledSize: new google.maps.Size(33, 33)
        }
      });
      marker.addListener("click", () => {
        this.ngZone.run(() => this.locationSelected.emit(location));
      });
      this.legacyMarkers.set(location, marker);
      markers.push(marker);
    }

    const clusterIconUrl = new ImageResolverPipe().transform(
      "/assets/icons/map/cluster-48px.svg"
    );
    this.clusterer = new (window as any).markerClusterer.MarkerClusterer({
      map: this.googleMap,
      markers,
      algorithm: new (window as any).markerClusterer.SuperClusterAlgorithm({
        minPoints: 4
      }),
      renderer: {
        render({
          count,
          position
        }: {
          count: number;
          position: google.maps.LatLng;
        }) {
          return new google.maps.Marker({
            position,
            icon: {
              url: clusterIconUrl,
              scaledSize: new google.maps.Size(48, 48)
            },
            label: {
              text: String(count),
              color: "black",
              fontSize: "12px"
            },
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count
          });
        }
      }
    });

    // Client location marker (not clustered)
    if (this.clientLocation()) {
      this.clientLegacyMarker = new google.maps.Marker({
        position: {
          lat: this.clientLocation().latitude,
          lng: this.clientLocation().longitude
        },
        map: this.googleMap,
        icon: {
          url: this.clientIconUrl,
          scaledSize: new google.maps.Size(33, 33)
        },
        clickable: false
      });
    }
  }

  private updateSelectedMarker(): void {
    for (const [location, marker] of this.legacyMarkers) {
      marker.setIcon({
        url: this.locationMatches(location, this.selectedLocation())
          ? this.selectedIconUrl
          : this.noSelectionIconUrl,
        scaledSize: new google.maps.Size(33, 33)
      });
    }
  }

  ngOnChanges(): void {
    this.init();
    if (this.noSelectionIconUrl) {
      this.updateSelectedMarker();
    }
  }

  init(): void {
    const selectedLocation = this.selectedLocation();
    if (!selectedLocation)
      this.mapLocation.set(
        this.clientLocation() ? this.clientLocation() : this.allLocations()[0]
      );
    else this.mapLocation.set(selectedLocation);
  }

  async ngAfterViewInit(): Promise<void> {
    const { Geocoder } = (await google.maps.importLibrary(
      "geocoding"
    )) as google.maps.GeocodingLibrary;

    const geocoder = new Geocoder();

    geocoder.geocode(
      {
        location: {
          lat: this.mapLocation().latitude,
          lng: this.mapLocation().longitude
        }
      },
      (results, status: string) => {
        if (status === "OK") {
          this.searchElementRef().nativeElement.value =
            results[0].formatted_address;
        }
      }
    );
  }

  toggleMap(): void {
    this.mapEnabled.update((v) => !v);
  }

  onMarkerClick(location: ILocation): void {
    this.locationSelected.emit(location);
  }

  onMapIdle(): void {
    if (!this.mapLocation()) return;
    this.locationIdled.emit(this.mapLocation());
  }

  useClientLocation(): void {
    if (!this.clientLocation()) return;
    const loc = this.clientLocation();
    this.mapLocation.set(loc);
    this.googleMapRef()?.googleMap?.panTo({
      lat: loc.latitude,
      lng: loc.longitude
    });
  }

  onSearch(value: string): void {
    if (!value) {
      this.suggestions.set([]);
      return;
    }
    this.searchSubject.next(value);
  }

  onSearchKeydown(event: KeyboardEvent, value: string): void {
    if (event.key === "Enter") {
      const current = this.suggestions();
      if (current.length > 0) {
        this.selectSuggestion(current[0]);
      } else {
        this.searchSubject.next(value);
      }
    } else if (event.key === "Escape") {
      this.suggestions.set([]);
    }
  }

  async selectSuggestion(
    suggestion: google.maps.places.AutocompleteSuggestion
  ): Promise<void> {
    this.suggestions.set([]);
    const place = suggestion.placePrediction.toPlace();
    await place.fetchFields({ fields: ["location", "formattedAddress"] });

    this.ngZone.run(() => {
      this.searchElementRef().nativeElement.value = place.formattedAddress;
      const loc = {
        longitude: place.location.lng(),
        latitude: place.location.lat()
      };
      this.mapLocation.set(loc);
      this.googleMapRef()?.googleMap?.panTo({
        lat: loc.latitude,
        lng: loc.longitude
      });
      this.locationSearched.emit(this.mapLocation());
    });
  }

  private async executeSearch(value: string): Promise<void> {
    if (!value) return;

    const { AutocompleteSuggestion } = (await google.maps.importLibrary(
      "places"
    )) as google.maps.PlacesLibrary;

    const { suggestions } =
      await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: value
      });

    this.ngZone.run(() => {
      this.suggestions.set(suggestions ?? []);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clusterer?.clearMarkers();
    this.legacyMarkers.forEach((m) => m.setMap(null));
    this.clientLegacyMarker?.setMap(null);
  }

  locationMatches(location: ILocation, location2: ILocation): boolean {
    return JSON.stringify(location) === JSON.stringify(location2);
  }
}
